// Carry Roll InverseSolver — back-solve launch angle for target carry (1.7).

import { useState } from 'react';
import { ExpressionInput, NumericDisplay } from 'calc-controls';
import { useStore } from '../../store';
import { useRequest } from '../../store/selectors';
import { solveInverse, solveInverseBallSpeed, type InverseResult } from '@carry-roll/shared-logic';
import { distanceToM, convertDistance } from '@carry-roll/shared-logic';
import { HelpTooltip } from '../calculator/HelpTooltip';

type SolveFor = 'launch' | 'ballspeed';

export function InverseSolver() {
  const request = useRequest();
  const distanceUnit = useStore((s) => s.distanceUnit);
  const [target, setTarget] = useState(0);
  const [solveFor, setSolveFor] = useState<SolveFor>('launch');
  const [result, setResult] = useState<InverseResult | null>(null);

  const handleSolve = () => {
    if (target <= 0) return;
    const targetM = distanceToM(target, distanceUnit);
    setResult(solveFor === 'ballspeed' ? solveInverseBallSpeed(targetM, request) : solveInverse(targetM, request));
  };

  const conv = (m: number) => Math.round(convertDistance(m, distanceUnit));

  return (
    <div data-testid="inverse-solver">
      <h3 className="subsection-title">
        Inverse solver
      </h3>
      <p className="text-sm mb-2" style={{ color: 'var(--color-text-muted)' }}>
        Enter a target carry to back-solve the optimal launch angle or ball speed.
      </p>
      <div className="flex gap-1 mb-3" role="group" aria-label="Solve for">
        {(['launch', 'ballspeed'] as const).map((s) => (
          <button
            key={s}
            type="button"
            role="radio"
            aria-checked={solveFor === s}
            className="btn"
            style={{
              fontWeight: solveFor === s ? 'var(--fw-bold)' : 'var(--fw-regular)',
              opacity: solveFor === s ? 1 : 0.6,
            }}
            onClick={() => setSolveFor(s)}
          >
            {s === 'launch' ? 'Launch angle' : 'Ball speed'}
          </button>
        ))}
      </div>
      <div className="flex gap-2 items-end">
        <div className="block flex-1">
          <span className="field-label">Target carry ({distanceUnit})</span>
          <ExpressionInput
            inline
            value={target}
            onChange={setTarget}
            unit={distanceUnit}
            aria-label="Target carry"
            decimals={0}
            min={0}
          />
        </div>
        <button type="button" className="btn" onClick={handleSolve}>
          Solve
        </button>
      </div>
      {result && (
        <div className="mt-3" data-testid={solveFor === 'ballspeed' ? 'inverse-solver-ballspeed' : 'inverse-result'}>
          <div
            className="badge inline-flex items-center"
            style={{
              background: result.verified ? 'var(--color-success)' : 'var(--color-warn)',
              color: 'white',
            }}
          >
            {result.verified ? 'Verified' : 'Unverified'}
            <HelpTooltip label="Inverse solver verification">Verified means the solved value reproduces the target carry within 0.5 m; Unverified means it could not match.</HelpTooltip>
          </div>
          <div className="mt-2 text-sm">
            {solveFor === 'ballspeed' ? (
              <>
                <strong>Ball speed:</strong>{' '}
                <NumericDisplay inline value={result.ballSpeedMs ?? 0} unit="m/s" decimals={1} />
              </>
            ) : (
              <>
                <strong>Launch angle:</strong>{' '}
                <NumericDisplay inline value={result.launchAngleDeg} unit="°" decimals={1} />
              </>
            )}
          </div>
          <div className="text-sm">
            <strong>Resulting carry:</strong>{' '}
            <NumericDisplay inline value={conv(result.carryM)} unit={distanceUnit} decimals={0} />
          </div>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
            {result.explanation}
          </p>
        </div>
      )}
    </div>
  );
}
